import { useEffect, useState } from 'react';
import {
  TextInput,
  Box,
  Checkbox,
  Group,
  Card,
  Stack,
  Accordion,
} from '@mantine/core';
import { MRT_Row } from 'mantine-react-table';
import { useTranslation } from '@refinedev/core';
import { validateBillingNumber } from '@billing/validation';
import { License, Payor, Provider } from 'interfaces';
import { getBillingNumberValidationError } from 'utils/billingNumberValidationUtils';

interface ProviderLicensesComponentProps {
  payors: Payor[];
  value?: License[];
  row?: MRT_Row<Provider>;
  siteID: string;
  licenses?: License[];
  onChange?: (licenses: License[]) => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const ProviderLicensesComponent = ({
  payors,
  siteID,
  row = null,
  licenses = [],
  onChange,
  onValidationChange,
}: ProviderLicensesComponentProps) => {
  const { translate } = useTranslation();
  const [checked, setChecked] = useState<string[]>(
    licenses.map((l) => l.payorID)
  );

  const [licenseMap, setLicenseMap] = useState<Record<string, string>>(
    licenses.reduce((acc, license) => {
      acc[license.payorID] = license.license || '';
      return acc;
    }, {} as Record<string, string>)
  );

  const handleCheckboxChange = (payorID: string, isNowChecked: boolean) => {
    if (isNowChecked) {
      setChecked((prev) => [...prev, payorID]);
    } else {
      setChecked((prev) => prev.filter((id) => id !== payorID));
      setLicenseMap((prev) => {
        const next = { ...prev };
        delete next[payorID];
        return next;
      });
    }
  };

  useEffect(() => {
    const newLicenses: License[] = checked.map((pid) => {
      const payor = payors.find((p) => p.id === pid);
      return {
        payorID: pid,
        payorName: payor?.name || '',
        license: licenseMap[pid] || '',
        payor,
        siteID,
      };
    });
    // Bubble up to parent (e.g., AddProviderModal) if provided
    if (onChange) onChange(newLicenses);
    // Maintain existing grid-editing integration via row cache when editing in table
    if (row) row._valuesCache.licenses = newLicenses;
  }, [checked, licenseMap, payors]);

  useEffect(() => {
    const isValid = checked.every((payorID) => {
      const payor = payors.find((candidate) => candidate.id === payorID);
      return (
        !!payor && !validateBillingNumber(payor, licenseMap[payorID] || '')
      );
    });
    onValidationChange?.(isValid);
  }, [checked, licenseMap, payors]);

  return (
    <Accordion
      multiple={false}
      defaultValue="licenses"
      variant="separated"
      radius="md"
      w={'100%'}
    >
      <Accordion.Item value="licenses">
        <Accordion.Control>Billing Numbers</Accordion.Control>
        <Accordion.Panel px={0}>
          <Card withBorder p="md" radius="md" mt="sm">
            <Stack gap="lg" align="center" justify="center">
              {payors.map((payor) => {
                const isChecked = checked.includes(payor.id);
                const billingNumber = licenseMap[payor.id] || '';
                const validationIssue = validateBillingNumber(
                  payor,
                  billingNumber
                );
                const validationError = getBillingNumberValidationError(
                  payor,
                  validationIssue,
                  translate
                );
                return (
                  <Group
                    key={payor.id}
                    gap="apart"
                    align="center"
                    w={'100%'}
                    justify="space-between"
                  >
                    <Checkbox
                      label={payor.name}
                      checked={isChecked}
                      onChange={(e) =>
                        handleCheckboxChange(payor.id, e.currentTarget.checked)
                      }
                    />

                    {isChecked && (
                      <Box style={{ flex: 1, maxWidth: 200 }}>
                        <TextInput
                          placeholder="Billing #"
                          value={billingNumber}
                          required
                          minLength={payor.billingNumberMinLength ?? undefined}
                          maxLength={payor.billingNumberMaxLength ?? undefined}
                          inputMode={
                            payor.billingNumberFormat === 'NUMERIC'
                              ? 'numeric'
                              : undefined
                          }
                          error={validationError}
                          onChange={(e) => {
                            const newLicense = e.currentTarget.value;
                            setLicenseMap((prev) => ({
                              ...prev,
                              [payor.id]: newLicense,
                            }));
                          }}
                        />
                      </Box>
                    )}
                  </Group>
                );
              })}
            </Stack>
          </Card>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
